// Pure helpers + constants for AddContentModal, extracted VERBATIM.

const tabPermissionMap = {
  announcements: 'course_content:announcements:manage',
  syllabus: 'course_content:syllabus:manage',
  slides: 'course_content:slides:manage',
  readings: 'course_content:readings:manage',
  recordings: 'course_content:recordings:manage',
  notifications: 'course_content:notifications:manage',
};

// Hard cap for recording uploads — S3's maximum object size (5 TB). Enforced
// here client-side and again server-side (MAX_S3_OBJECT_BYTES in
// server/routes/course-content.js). A single uploaded file may never exceed it.
const MAX_RECORDING_UPLOAD_BYTES = 5 * 1024 * 1024 * 1024 * 1024; // 5 TB

// Largest document (reading, slide, syllabus) that can be uploaded. Mirrors
// MAX_CONTENT_UPLOAD_BYTES in server/routes/courseContentSafety.js.
const MAX_DOCUMENT_UPLOAD_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB

// An elective recording is sent through the server like a document (course recordings go straight to storage), so it
// has the same 2 GB limit. Past it the upload would run to the end and only then be refused.
const MAX_ELECTIVE_RECORDING_UPLOAD_BYTES = MAX_DOCUMENT_UPLOAD_BYTES;

// The largest recording that can be uploaded here, and how to say so.
const recordingUploadLimit = (elective) => (elective
  ? { bytes: MAX_ELECTIVE_RECORDING_UPLOAD_BYTES, message: 'Elective recordings can be at most 2 GB. Compress the file, or add it as an external video link.' }
  : { bytes: MAX_RECORDING_UPLOAD_BYTES, message: 'Recording files cannot exceed 5 TB.' });

const MAX_LINK_LENGTH = 500;

// The server refuses a description over 5000 characters, counting the formatting tags, links and images the editor adds.
const MAX_DESCRIPTION_LENGTH = 5000;
const descriptionLength = (description) => {
  const html = description && typeof description === 'object' ? (description.content_html || description.html || '') : description;
  return typeof html === 'string' ? html.trim().length : 0;
};

// The same rules the server applies to readings/slides/syllabus links: http or https
// only, up to 500 characters; a bare "example.com/page" becomes https://example.com/page. (This check is a little
// stricter than the server's: a bare word with no dot, like "abc", is refused here as a likely typo.)
// Returns the normalized link, '' for an empty value, or null when it isn't acceptable.
const normalizeContentLink = (value) => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return '';
  if (text.length > MAX_LINK_LENGTH) return null;
  const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(text);
  const withScheme = hasScheme ? text : `https://${text}`;
  try {
    const parsed = new URL(withScheme);
    if ((parsed.protocol !== 'http:' && parsed.protocol !== 'https:') || !parsed.hostname) return null;
    // A bare word such as "abc" is a typo, not an address: without https:// typed, the site needs a dot (example.com).
    if (!hasScheme && !parsed.hostname.includes('.') && parsed.hostname !== 'localhost') return null;
  } catch (error) {
    return null;
  }
  return withScheme.length > MAX_LINK_LENGTH ? null : withScheme;
};

const LINK_ERROR_MESSAGE = 'Links must be valid http or https addresses of up to 500 characters.';

const sortFilesByLastModified = (files) => [...files].sort((a, b) => (a.lastModified || 0) - (b.lastModified || 0));

const formatFileTimestamp = (timestamp) => {
  if (!timestamp) return 'No timestamp';
  try {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (e) {
    return 'No timestamp';
  }
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

// Recording size shown in the content list: MB until it reaches 1000 MB, then GB.
const formatRecordingFileSize = (bytes) => {
  const mb = bytes / 1024 / 1024;
  if (mb >= 1000) {
    return `${(mb / 1024).toFixed(2)} GB`;
  }
  return `${mb.toFixed(2)} MB`;
};

// The final S3 file name an uploaded recording will get, derived from its date
// (mirrors the server's recordingDateFileName: "MM_DD_YYYY.mp4"). Used to detect
// a collision with an existing recording before uploading.
const recordingFinalFileName = (recordingDate) => {
  const match = typeof recordingDate === 'string' ? recordingDate.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
  return match ? `${match[2]}_${match[3]}_${match[1]}.mp4` : null;
};

export {
  tabPermissionMap,
  MAX_RECORDING_UPLOAD_BYTES,
  MAX_DOCUMENT_UPLOAD_BYTES,
  MAX_ELECTIVE_RECORDING_UPLOAD_BYTES,
  recordingUploadLimit,
  normalizeContentLink,
  MAX_DESCRIPTION_LENGTH,
  descriptionLength,
  LINK_ERROR_MESSAGE,
  sortFilesByLastModified,
  formatFileTimestamp,
  formatFileSize,
  formatRecordingFileSize,
  recordingFinalFileName,
};
